import { useEffect, useMemo, useState } from "react";
import { DarwinHeader } from "build-ts/common";
import { UserProto } from "build-ts/interface";
import { fetchChatSession } from "services/AgentChatService";
import { listUsers } from "services/UserService";

export interface RunRef {
  runId: string;
  sourceChatId: string;
}

// Module-level promise cache — all hook instances share a single users fetch.
// On failure the cache is cleared so the next mount can retry.
let cachedUsersPromise: Promise<UserProto[]> | null = null;

const getCachedUsers = (): Promise<UserProto[]> => {
  if (!cachedUsersPromise) {
    cachedUsersPromise = listUsers().catch(err => {
      cachedUsersPromise = null;
      throw err;
    });
  }
  return cachedUsersPromise;
};

/**
 * For each run, resolves the author display name from the run's source chat session header.
 *
 * Returns a map of runId → author display name.
 */
const useRunAuthorNames = (runs: RunRef[]): Record<string, string> => {
  const [users, setUsers] = useState<UserProto[]>([]);
  const [sessionHeaders, setSessionHeaders] = useState<Record<string, DarwinHeader>>({});

  // Load the user list once on mount.
  useEffect(() => {
    getCachedUsers()
      .then(setUsers)
      .catch(() => {});
  }, []);

  // Fetch session headers for all runs whenever the runs list changes.
  useEffect(() => {
    if (runs.length === 0) return;

    Promise.allSettled(
      runs.map(r =>
        fetchChatSession(r.sourceChatId, false, false)
          .then(res => ({ sessionId: r.sourceChatId, header: res.session_header }))
          .catch(() => null)
      )
    ).then(results => {
      const newHeaders: Record<string, DarwinHeader> = {};
      for (const result of results) {
        if (result.status === "fulfilled" && result.value?.header) {
          newHeaders[result.value.sessionId] = result.value.header;
        }
      }
      if (Object.keys(newHeaders).length > 0) {
        setSessionHeaders(prev => ({ ...prev, ...newHeaders }));
      }
    });
  }, [runs]);

  // Derive runId → author name from the users and sessionHeaders states.
  return useMemo(() => {
    const userMap: Record<string, string> = {};
    for (const u of users) {
      if (u.header?.id) userMap[u.header.id] = u.header.name ?? "";
    }
    const map: Record<string, string> = {};
    for (const r of runs) {
      const header = sessionHeaders[r.sourceChatId];
      if (header?.author_id) {
        map[r.runId] = userMap[header.author_id] ?? "";
      }
    }
    return map;
  }, [runs, users, sessionHeaders]);
};

export default useRunAuthorNames;
